import { useCallback, useEffect, useRef, useState } from "react"
import { XMarkIcon } from "@heroicons/react/20/solid"
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline"
import { cn } from "@/lib/cn"
import type { ToastItem, ToastType } from "@/types/toast"

const DEFAULT_AUTO_DISMISS_MS = 5000
// Matches the exit transition duration below so the node is removed only after
// it has animated out.
const LEAVE_MS = 200

interface ToastStyle {
  container: string
  icon: string
  Icon: typeof CheckCircleIcon
}

const TOAST_STYLES: Record<ToastType, ToastStyle> = {
  success: {
    container: "border-green-200 bg-green-50 text-green-800",
    icon: "text-green-500",
    Icon: CheckCircleIcon,
  },
  error: {
    container: "border-red-200 bg-red-50 text-red-800",
    icon: "text-red-500",
    Icon: ExclamationCircleIcon,
  },
  warning: {
    container: "border-yellow-200 bg-yellow-50 text-yellow-800",
    icon: "text-yellow-500",
    Icon: ExclamationTriangleIcon,
  },
  info: {
    container: "border-blue-200 bg-blue-50 text-blue-800",
    icon: "text-blue-500",
    Icon: InformationCircleIcon,
  },
}

interface ToastProps {
  toast: ToastItem
  autoDismissMs?: number
  onDismiss: (id: string) => void
}

export function Toast({
  toast,
  autoDismissMs = DEFAULT_AUTO_DISMISS_MS,
  onDismiss,
}: ToastProps): JSX.Element {
  const [entered, setEntered] = useState(false)
  const [leaving, setLeaving] = useState(false)
  const dismissed = useRef(false)

  const dismiss = useCallback(() => {
    if (dismissed.current) return
    dismissed.current = true
    setLeaving(true)
    window.setTimeout(() => onDismiss(toast.id), LEAVE_MS)
  }, [onDismiss, toast.id])

  useEffect(() => {
    const frame = requestAnimationFrame(() => setEntered(true))
    const timer = window.setTimeout(dismiss, autoDismissMs)
    return () => {
      cancelAnimationFrame(frame)
      window.clearTimeout(timer)
    }
  }, [autoDismissMs, dismiss])

  const style = TOAST_STYLES[toast.type]
  const Icon = style.Icon
  const isError = toast.type === "error"

  return (
    <div
      role={isError ? "alert" : "status"}
      aria-live={isError ? "assertive" : "polite"}
      data-testid="toast"
      data-toast-type={toast.type}
      className={cn(
        "pointer-events-auto w-full rounded-lg border p-4 shadow-lg ring-1 ring-black/5 transition duration-200 ease-out",
        style.container,
        entered && !leaving ? "translate-y-0 opacity-100" : "-translate-y-2 opacity-0"
      )}
    >
      <div className="flex items-start gap-3">
        <Icon className={cn("h-5 w-5 flex-shrink-0", style.icon)} aria-hidden="true" />
        <p className="flex-1 text-sm font-medium">{toast.message}</p>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss notification"
          className="-m-1 inline-flex flex-shrink-0 rounded-md p-1 opacity-70 transition hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-current"
        >
          <XMarkIcon className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>
    </div>
  )
}
